/**
 * FloView — 공급 흐름도의 핵심 인터랙티브 뷰(이동·확대·범례·전체 화면·클릭 계보·조작 안내).
 * 이동·확대와 고정 거터·눈금자는 FloCanvas 가, 그림은 FloSvg 가,
 *   조작 안내 줄은 FloViewHints 가 맡는다.
 * 크게 보기 창(FloDialog)은 이 컴포넌트를 그대로 담으므로 둘의 모양과 동작이 같다.
 * 전체 화면은 이 컴포넌트의 루트에 걸리므로 안내 줄도 전체 화면에 함께 들어간다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 *
 * props
 *   graph           좌표 없는 의미 그래프(floModel 헤더 참조).
 *                   렌더마다 새로 만들지 말고 useMemo 로 고정한다.
 *   height          숫자(px) 또는 CSS 길이 문자열. 기본 480. 부모 높이를 채우려면 "100%".
 *   lineage         노드·연결 클릭 시 계보 강조. 기본 true.
 *   legend          툴바 왼쪽 범례. 기본 true.
 *   hints           캔버스 아래 조작 안내 줄. 기본 true.
 *   foldLimit       잎 접기 기준(buildFloModel 과 같다). 기본 6.
 *   laneTypeOrder   레인 유형 정렬 순서(buildFloModel 과 같다).
 *   locationLabels  운송이 아닌 노드 둘째 줄에 sub(없으면 레인 이름)를 보인다. 기본 false.
 * 사용법·그래프 계약: saas-web/.claude/skills/flo/SKILL.md
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

import useFlo from "@plannel/components/flo/useFlo";
import FloCanvas, { FLO_TOOL_BTN_SX } from "@plannel/components/flo/FloCanvas";
import FloSvg from "@plannel/components/flo/FloSvg";
import FloViewLegend from "@plannel/components/flo/FloViewLegend";
import FloViewHints from "@plannel/components/flo/FloViewHints";

/** 브라우저 Fullscreen API 를 ref 대상 엘리먼트 하나에 건다. */
function useElementFullscreen(ref) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onChange = () => setActive(document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [ref]);

  const enter = useCallback(() => {
    Promise.resolve(ref.current?.requestFullscreen?.()).catch(() => {});
  }, [ref]);
  const exit = useCallback(() => {
    document.exitFullscreen?.();
  }, []);

  return { active, enter, exit };
}

export default function FloView({
  graph, height = 480, lineage = true, legend = true, hints = true, foldLimit = 6, laneTypeOrder,
  locationLabels = false,
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const { model, geometry, edges, fonts } = useFlo(graph, { foldLimit, laneTypeOrder });
  const [sel, setSel] = useState(null);
  const { active: isFs, enter, exit } = useElementFullscreen(rootRef);

  if (!model || !geometry) return null;

  const fullscreenSupported = typeof document !== "undefined" && !!document.fullscreenEnabled;
  const fsLabel = t(isFs ? "exitFullScreen" : "fullScreen");

  return (
    <Box data-flo-view ref={rootRef} sx={{
      height, width: "100%", minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column",
      "&:fullscreen": { width: "100vw", height: "100vh", bgcolor: "background.paper" },
    }}>
      {/* 안내 줄이 높이를 가져가도 캔버스가 남은 높이를 채우게 감싼다 —
          맞춤은 FloCanvas 의 크기 관찰이 다시 한다. */}
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        <FloCanvas
          geometry={geometry}
          fonts={fonts}
          legend={legend ? <FloViewLegend model={model} fonts={fonts} /> : null}
          controls={fullscreenSupported && (
            <Tooltip title={fsLabel} PopperProps={{ disablePortal: true }}>
              <IconButton size="small" sx={FLO_TOOL_BTN_SX} onClick={isFs ? exit : enter} aria-label={fsLabel}>
                {isFs ? <FullscreenExitIcon sx={{ fontSize: 15 }} /> : <FullscreenIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
          fitKey={`${geometry.width}x${geometry.height}-${isFs}`}
          onBackgroundClick={lineage ? () => setSel(null) : undefined}
        >
          <FloSvg model={model} geometry={geometry} edges={edges} fonts={fonts} dotGrid
                  viewBox={`0 0 ${geometry.width} ${geometry.height}`}
                  width={geometry.width} height={geometry.height} style={{ display: "block" }}
                  markerBase={`flo-${uid}`}
                  selection={lineage ? sel : null} onSelect={lineage ? setSel : undefined}
                  locationLabels={locationLabels} />
        </FloCanvas>
      </Box>
      {hints && <FloViewHints lineage={lineage} selected={lineage && !!sel} />}
    </Box>
  );
}
